"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell, {
  type BirthToolFormShellProps,
} from "@/components/tool-shell/BirthToolFormShell";

import type { LalKitabHouseCheckTarget } from "./LalKitabHouseCheck";
import {
  LAL_KITAB_HOUSE_CHECK_FIELD_IDS,
  LAL_KITAB_HOUSE_CHECK_FORM,
  LAL_KITAB_HOUSE_CHECK_FRAME,
  LAL_KITAB_HOUSE_CHECK_HI_FORM,
  LAL_KITAB_HOUSE_FORM_RESERVE,
} from "./lal-kitab-house-check-form";

// The Lal Kitab house check's one boundary (the /graha/ hub's pattern,
// GrahaChartCheckLazy): a page's server section imports only this module, and
// it owns the one dynamic() to the island. The form, place search, Lal Kitab
// client, reader, answer and analytics load as an async chunk; the first load
// gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the page never downloads the island; a press, focus or
// submit on the shell does.
//
// The slot reserves the form's tallest state (LAL_KITAB_HOUSE_FORM_RESERVE,
// the career check's form: no name field, a required birth time), so neither
// shell -> form nor an engine error after a slow reply moves the page below;
// the answer's taller room is added by the island in the press that sends the
// request. translate="no" covers the shell, form and answer, which re-render
// on "Check another birth date" (tests/translate-safe-islands.test.ts).

type CheckLanguage = "en" | "hi";

/** Each locale's shell: the live form's words in the shared frame. */
const SHELL: Readonly<Record<CheckLanguage, BirthToolFormShellProps>> = {
  en: { ...LAL_KITAB_HOUSE_CHECK_FORM, className: LAL_KITAB_HOUSE_CHECK_FRAME },
  hi: { ...LAL_KITAB_HOUSE_CHECK_HI_FORM, className: LAL_KITAB_HOUSE_CHECK_FRAME },
};

/**
 * The shell's props for this page's language. dynamic()'s loading placeholder
 * receives no props from the page, so the slot hands them down through context.
 */
const ShellProps = createContext<BirthToolFormShellProps>(SHELL.en);

function LoadingShell() {
  return <BirthToolFormShell {...useContext(ShellProps)} />;
}

const LalKitabHouseCheck = dynamic(() => import("./LalKitabHouseCheck"), {
  ssr: false,
  loading: () => <LoadingShell />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: LAL_KITAB_HOUSE_CHECK_FIELD_IDS.date,
  tob: LAL_KITAB_HOUSE_CHECK_FIELD_IDS.time,
  place: LAL_KITAB_HOUSE_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || LAL_KITAB_HOUSE_CHECK_FIELD_IDS.date;
}

export default function LalKitabHouseCheckLazy(props: LalKitabHouseCheckTarget) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const shell = SHELL[props.language ?? "en"];

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit, and never after a touch, which may start a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(LAL_KITAB_HOUSE_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-lal-kitab-check-slot=""
      className={`flex flex-col ${LAL_KITAB_HOUSE_FORM_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <ShellProps.Provider value={shell}>
          <LalKitabHouseCheck {...props} focusFieldId={focusFieldId} />
        </ShellProps.Provider>
      ) : (
        <BirthToolFormShell {...shell} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
